/* Confirm — the dialog that stands between a click and something irreversible.
 *
 * WHY THIS EXISTS. Deleting a post was a single click on a menu item. A menu
 * opened by accident, a mis-aimed tap, and somebody's post was gone with no
 * undo and no warning. That is exactly what happened during development, and
 * the person it happened to could not tell whether they had deleted it or the
 * app had lost it.
 *
 * ONE COMPONENT FOR EVERY DESTRUCTIVE ACTION, so none of them can quietly skip
 * the step. Deleting a post, withdrawing an invitation, removing an event —
 * all of them go through here.
 *
 * The confirm button is focused on open, so Enter confirms and Escape cancels.
 * `tone` decides the colour: 'danger' for anything that destroys content,
 * 'primary' for something merely significant.
 */
import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'

export interface ConfirmProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** What will happen, in one sentence. Say the consequence, not the action. */
  body?: ReactNode
  /** The verb, not "OK" — "Delete post" reads as a decision, "OK" does not. */
  confirmLabel: string
  cancelLabel?: string
  tone?: 'danger' | 'primary'
  loading?: boolean
  onConfirm: () => void
}

export function Confirm({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'danger',
  loading = false,
  onConfirm,
}: ConfirmProps) {
  const confirmRef = useRef<HTMLButtonElement>(null)

  // Focused rather than autoFocus: Radix moves focus into the dialog itself on
  // open, and an autoFocus attribute races with it.
  useEffect(() => {
    if (open) confirmRef.current?.focus()
  }, [open])

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      width="420px"
      footer={
        <div className="flex gap-s2">
          <Button
            variant="secondary"
            size="lg"
            full
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="lg"
            full
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {body ? (
        <p className="px-s4 pb-s2 pt-s3 text-base leading-normal text-ink-2">
          {body}
        </p>
      ) : null}
    </Modal>
  )
}
